import type { Surface } from '@funkit/connect-core'
import { useCallback, useEffect, useRef } from 'react'
import { AppState, Linking } from 'react-native'

import { logger } from '../../logging/logger'
import { useFunkitTranslation } from '../../providers/FunkitI18nProvider'
import { FiatStatusScreen } from '../FiatStatusScreen'
import type { SurfaceAdapter, SurfaceContext } from './adapter'

interface RedirectParams {
  checkoutUrl: string
  /** Log context only — the contract says URL surfaces may carry none. */
  environment: string
}

function matchParams(surface: Surface): RedirectParams | undefined {
  if (surface.kind !== 'URL_REDIRECT' || surface.provider !== 'BANXA') {
    return undefined
  }
  // The contract requires `params`, but no response is validated before it
  // reaches here, so an absent one must fail the match rather than throw.
  const params: unknown = surface.params
  if (params === null || typeof params !== 'object') {
    return undefined
  }
  const { checkoutUrl, environment } = params as Record<string, unknown>
  // The URL is handed to the OS, so a non-https scheme would let a response
  // open an arbitrary deep link.
  if (typeof checkoutUrl !== 'string' || !checkoutUrl.startsWith('https://')) {
    return undefined
  }
  return {
    checkoutUrl,
    environment: typeof environment === 'string' ? environment : 'unstated',
  }
}

/** Banxa's hosted page collects KYC and takes payment outside the app. */
export const banxaRedirect: SurfaceAdapter = (surface) => {
  const params = matchParams(surface)
  if (params === undefined) {
    return undefined
  }

  return {
    control: 'HANDOFF',
    render: (context) => <BanxaRedirect params={params} context={context} />,
  }
}

function BanxaRedirect({
  params,
  context,
}: {
  params: RedirectParams
  context: SurfaceContext
}) {
  const { t } = useFunkitTranslation()
  const open = useOpenCheckout(params, context.onFailed)

  // Banxa kills the URL about a minute after minting it, so it cannot wait
  // behind a tap.
  useEffect(() => {
    open()
  }, [open])

  // The surface yields nothing (`expects: []`), so coming back is the whole
  // signal — bookkeeping, never evidence of payment. The order's own status
  // is what says whether money moved, which the poll that follows reads.
  useReturnFromBrowser(useCallback(() => context.report({}), [context.report]))

  return (
    <FiatStatusScreen
      tone="waiting"
      title={t('fiat.redirectTitle')}
      detail={t('fiat.redirectDetail')}
      cta={{ label: t('fiat.redirectReopen'), onPress: open }}
      onClose={context.onClose}
    />
  )
}

function useOpenCheckout(
  { checkoutUrl, environment }: RedirectParams,
  onFailed: (code: string) => void,
): () => void {
  const fail = useRef(onFailed)
  fail.current = onFailed

  return useCallback(() => {
    logger.info('connect-rn:fiat:banxa_redirect_opened', { environment })
    Linking.openURL(checkoutUrl).catch((caught: unknown) => {
      logger.warn('connect-rn:fiat:banxa_redirect_open_failed', {
        environment,
        reason: caught instanceof Error ? caught.name : 'unknown',
      })
      fail.current('open_failed')
    })
  }, [checkoutUrl, environment])
}

/**
 * Runs when the app is foregrounded, once per trip out to the browser.
 *
 * Only `background` counts as leaving. iOS also reports `inactive` for the
 * notification shade, the app switcher and call banners, and none of those
 * leave the app — treating them as a return would advance the flow past a
 * payment the user never made. Armed again on each return, because the reopen
 * CTA starts a second trip that has to be able to report too.
 */
function useReturnFromBrowser(onReturn: () => void): void {
  const returned = useRef(onReturn)
  returned.current = onReturn

  useEffect(() => {
    let away = false

    const subscription = AppState.addEventListener('change', (next) => {
      if (next === 'background') {
        away = true
        return
      }
      if (next !== 'active' || !away) {
        return
      }
      away = false
      returned.current()
    })
    return () => subscription.remove()
  }, [])
}
